/** @file -L/-R counterparts per kind by name; unpaired entities and pinned socket pairs map to themselves. */
import type { Doc, DocIndex, Id } from '@pose2d/format/types';
import { mirrorName } from '@pose2d/format/doc/mirrorName';

/** Counterpart lookups used by mirrorAnimation (property functions, safe to pass as callbacks). */
export type SideCounterparts = {
  bone: (id: Id) => Id;
  slot: (id: Id) => Id;
  ik: (id: Id) => Id;
  attachment: (id: Id) => Id;
  /** The placeholder an attachment key of source slot `slotId` shows on the counterpart slot. */
  placeholder: (slotId: Id, name: string) => string;
};

type Paired = 'bone' | 'slot' | 'ik' | 'attachment';

/** Lookups over `doc` and its pre-command `index`; `keepItems` pins every pair with a socket bone on either side. */
export function sideCounterparts(doc: Doc, index: DocIndex, keepItems: boolean): SideCounterparts {
  const named = (kind: Paired, id: Id, name: string | undefined): Id => {
    const other = name === undefined ? null : mirrorName(name);
    return (other === null ? undefined : index.byName.get(kind)?.get(other)) ?? id;
  };
  const socketBone = (boneId: Id | undefined): boolean => {
    const kind = boneId === undefined ? undefined : doc.bones[boneId]?.kind;
    return keepItems && (kind === 'socket' || kind === 'socketGrip2');
  };
  // A pair is pinned as a whole (either side on a socket bone), so the mapping stays a bijection.
  const boneOther = (id: Id): Id => named('bone', id, doc.bones[id]?.name);
  const slotOther = (id: Id): Id => named('slot', id, doc.slots[id]?.name);
  const slotPinned = (id: Id): boolean => socketBone(doc.slots[id]?.bone) || socketBone(doc.slots[slotOther(id)]?.bone);
  return {
    bone: (id) => (socketBone(id) || socketBone(boneOther(id)) ? id : boneOther(id)),
    slot: (id) => (slotPinned(id) ? id : slotOther(id)),
    ik: (id) => named('ik', id, doc.constraints[id]?.name),
    attachment: (id) => named('attachment', id, doc.attachments[id]?.name),
    placeholder: (slotId, name) => (slotPinned(slotId) ? name : (mirrorName(name) ?? name)),
  };
}
